import { HowTo } from '../types';

/**
 * Account and device How Tos — the PIN, the active user, and the lock.
 *
 * These run where there is often NO active user yet (the profile picker), so
 * none of them may carry a `requiredRole`: roles are read from the active
 * user, and filtering on one would hide the guide from exactly the person who
 * needs it. They stay 'staff' rather than 'all': an external member has no
 * separate PIN story yet, and widening the audience here would put the first
 * four guides in front of them without anyone deciding that.
 */
export const accountHowTos: HowTo[] = [
  {
    key: 'account.why-pin',
    version: 1,
    section: 'Security',
    title: 'Why am I being asked for a PIN?',
    summary:
      'What the profile screen is for, why this machine asks who you are, and why it locks itself.',
    keywords: [
      'pin', 'why', 'what', 'screen', 'profile', 'profiles', 'choose', 'picker',
      'who', 'active', 'user', 'shared', 'device', 'machine', 'computer',
      'lock', 'locked', 'locking', 'timeout', 'inactivity', 'idle', 'kicked',
      'thrown out', 'signed out', 'password', 'again', 'confused', 'what is',
    ],
    routes: ['/staff-select'],
    audience: 'staff',
    availableSignedOut: true,
    steps: [
      {
        text: 'This machine is signed in once. The tiles are the people who can work on it, so the app asks which of you is at the keyboard.',
        target: 'account.profile-tiles',
      },
      {
        text: 'That is what the PIN is for. Your **password** signs the machine in, once. Your **PIN** switches between people afterwards, and only works on a machine already signed in.',
      },
      {
        text: 'Whoever is picked here is the active user. Posts, attendance and reports are all recorded against them.',
      },
      {
        text: 'You are here because somebody chose **Switch user**, or the machine locked itself and somebody chose it from the lock.',
      },
      {
        text: 'The two differ. Your own PIN at the lock returns you to everything you had open. Coming out to this screen starts fresh, so unsaved work is lost — let the last person save before you take over.',
      },
      {
        text: 'The machine locks itself because it is shared and rarely closed. How long it waits is set by an Admin.',
      },
    ],
    related: ['account.forgot-pin'],
  },
  {
    key: 'account.switch-user',
    version: 1,
    section: 'Security',
    title: 'Hand the device to someone else',
    summary:
      'Switch the app to another member of staff, so what gets written next is recorded against the right person.',
    youNeed: ['Your own PIN, to get back in afterwards'],
    keywords: [
      'switch', 'change', 'swap', 'hand over', 'handover', 'another', 'other',
      'user', 'staff', 'member', 'colleague', 'profile', 'active', 'pin',
      'lock', 'device', 'shared', 'log out', 'logout', 'sign out', 'me',
      'wrong person', 'not me',
    ],
    // Starts inside the app, at the avatar menu — so it is listed there, not
    // on the profile picker. By the time someone is at the picker they have
    // already done the only step that needed finding.
    routes: ['/'],
    audience: 'staff',
    steps: [
      {
        text: 'Posts and attendance are recorded against whoever is active, so handing the machine over without switching puts your colleague\'s work under your name.',
      },
      {
        text: 'The pill at the top right shows who is active. Click it to reach the profile picker.',
        target: 'account.switch-pill',
      },
      {
        text: 'The profile picker opens. Your colleague picks their own tile.',
        target: 'account.profile-tiles',
        route: '/staff-select',
      },
      {
        text: 'They enter their PIN and the app starts fresh, so save anything unfinished before you hand over.',
      },
      {
        text: 'If the machine has locked and the keypad shows your name, your colleague does not need your PIN. **Switch user** takes them to the picker and their own tile.',
      },
      {
        // Kept to the end deliberately: it is the alternative to everything
        // above, not a stage of it. Put mid-guide, the next step announced that
        // the profile picker opens — which locking does not do.
        text: 'Only stepping away? Use **Lock device**, under the profile icon. You stay active, and your PIN returns you to exactly what you had open.',
        target: ['account.lock-device', 'account.profile-icon'],
      },
    ],
    related: ['account.forgot-pin', 'account.why-locked'],
  },
  {
    key: 'account.set-up-pin',
    version: 1,
    section: 'Security',
    title: 'Set up your PIN',
    summary:
      'Choose the 4-digit PIN you will use to sign in on a shared machine.',
    youNeed: ['Your email address and password for Rustle It'],
    keywords: [
      'pin', 'set up', 'setup', 'create', 'new', 'first', 'first time',
      'choose', 'code', 'passcode', 'number', '4 digit', 'four digit',
      'profile', 'account', 'never', 'no pin',
    ],
    // Deliberately listed on NO page. Whether you need this depends on
    // `hasPin`, which is a fact about one profile — the help list cannot know
    // it, and listing this on the picker offered it to everyone, nearly all of
    // whom set their PIN months ago. The app knows, so it asks: the setup
    // prompt itself carries the offer, and it only appears for someone who has
    // no PIN. Still reachable by search, and by the "forgot your PIN" guide,
    // which hands over to it at the point the two flows converge.
    routes: [],
    audience: 'staff',
    availableSignedOut: true,
    steps: [
      {
        text: 'On the profile picker, a tile marked **Set up a PIN** is one that has never had one. Click it.',
        target: 'account.profile-tiles',
        route: '/staff-select',
      },
      {
        text: 'It offers to take you to sign in. The machine signs out at that point, which is normal.',
      },
      {
        text: 'Sign in with your **email address and password**. This is the proof that the tile is really you; the PIN that comes next is only a shortcut for this device.',
      },
      {
        text: 'Choose a **4-digit PIN** and type it again to confirm. Pick something you will remember without writing it down — anyone who can see it can write posts as you.',
      },
      {
        text: 'You land in the app. From now on your tile takes the PIN instead of your password, and your password is only needed if the PIN is ever cleared.',
      },
    ],
    related: ['account.forgot-pin', 'account.switch-user'],
  },
  {
    key: 'account.forgot-pin',
    version: 1,
    section: 'Security',
    title: "You've forgotten your PIN",
    summary:
      'Clear a PIN you cannot remember and set a new one — and what it costs everyone else on the device.',
    youNeed: ['Your email address and password for Rustle It'],
    keywords: [
      'forgot', 'forgotten', 'forget', 'lost', 'remember', 'cannot remember',
      'wrong', 'locked out', 'reset', 'clear', 'change', 'new', 'pin', 'code',
      'passcode', 'blocked', 'too many', 'attempts',
    ],
    routes: ['/staff-select'],
    audience: 'staff',
    availableSignedOut: true,
    steps: [
      {
        text: 'Pick your tile on the profile picker as usual — the keypad is where the way out lives.',
        target: 'account.profile-tiles',
        route: '/staff-select',
      },
      {
        text: 'Click **Forgot PIN?** under the keypad.',
        target: 'account.forgot-pin',
      },
      {
        text: 'You are asked to confirm your **email address and password** — proof it is you, since a PIN anyone could clear from the keypad would protect nothing.',
      },
      {
        text: 'The button is **Reset PIN & sign out**. If a colleague is mid-task on this machine, let them save first.',
      },
      {
        text: 'Sign in again and you are asked to choose a new PIN, exactly as you did the first time.',
        seeAlso: { guideKey: 'account.set-up-pin', label: 'Show me the PIN setup steps' },
      },
      {
        text: 'Three wrong PINs in a row also signs the device out, rather than locking you out permanently. Same road back: sign in, set a new PIN.',
      },
    ],
    related: ['account.set-up-pin', 'account.switch-user'],
  },
  {
    key: 'account.why-locked',
    version: 1,
    section: 'Security',
    title: 'Why does it keep asking for my PIN?',
    summary:
      'What the lock is for, why it appears when it does, and who can change how long it waits.',
    keywords: [
      'lock', 'locked', 'locking', 'keeps', 'again', 'always', 'annoying',
      'timeout', 'time out', 'idle', 'inactivity', 'asks', 'asking', 'pin',
      'every time', 'too often', 'sleep', 'screen',
    ],
    // An in-app explainer. Read after you have unlocked and wondered why —
    // the help window is deliberately hidden while the device is locked.
    routes: ['/'],
    audience: 'staff',
    steps: [
      {
        text: 'The keypad appears after a spell with no typing or clicking. Nothing is lost: everything you had open is behind it, and your PIN brings it back.',
      },
      {
        text: 'It exists because these machines are shared and rarely closed. Without it, a screen left open is an open record of every child in the setting.',
      },
      {
        // Named in words, with no target and no route: most readers of this
        // guide are not Admins, and offering "Take me there" to a screen they
        // cannot open would be a dead end dressed up as help.
        text: 'How long it waits before locking can be changed — it is a setting your organisation\'s Admin controls.',
      },
      {
        text: 'For a deliberate break, **Lock device** locks it there and then rather than leaving you to wait out the timer. It is under the profile icon at the top right.',
        target: ['account.lock-device', 'account.profile-icon'],
      },
    ],
    related: ['account.switch-user'],
  },
  {
    key: 'account.which-exit',
    version: 1,
    section: 'Security',
    title: 'Switch user, Lock device or Sign out?',
    summary:
      'Three controls an inch apart, with three different consequences. Which one you want, and what signing out costs on a shared machine.',
    keywords: [
      'sign out', 'signout', 'log out', 'logout', 'exit', 'leave', 'quit',
      'finish', 'end of day', 'going home', 'lock', 'locked', 'switch',
      'change user', 'difference', 'which', 'wrong one', 'accidentally',
      'shared', 'device', 'machine', 'password', 'stuck',
    ],
    routes: ['/'],
    audience: 'staff',
    steps: [
      {
        text: 'Three controls sit together at the top right. The pill carries whoever is active; the profile icon beside it holds the other two.',
        target: 'account.switch-pill',
      },
      {
        text: '**Lock device** is for stepping away. You stay active, and your PIN brings you back to what you had open.',
        target: ['account.lock-device', 'account.profile-icon'],
      },
      {
        text: 'The **pill** is for handing over. Your colleague picks their own tile and PIN, which keeps their work under their name. The app starts fresh, so save first.',
        target: 'account.switch-pill',
      },
      {
        text: '**Sign out** signs out the **machine**, not just you. Everyone loses the PIN route until somebody signs in again with their own email and password.',
        target: ['account.sign-out', 'account.profile-icon'],
      },
      {
        text: 'It is open to anyone at the machine on purpose: whoever is last at the end of the day should be able to shut it properly.',
      },
      {
        text: 'So at the end of a shift, lock it or switch away from yourself. Sign out when the machine is leaving the setting.',
      },
      {
        text: 'Done it by mistake? Nothing is lost. Any member of staff can sign back in with their own details, and everyone returns to their tile.',
      },
    ],
    related: ['account.switch-user', 'account.why-locked'],
  },
  {
    key: 'account.device-password',
    version: 1,
    section: 'Security',
    title: 'Change your password',
    summary:
      'Change the password you sign in with, and add the second step that protects it.',
    youNeed: [
      'Your current password',
      'To be the one this machine is signed in as — see the last step',
    ],
    keywords: [
      'password', 'change password', 'new password', 'reset', 'security',
      'two factor', 'two-factor', '2fa', 'mfa', 'authenticator', 'code',
      'device', 'account', 'login', 'sign in', 'credentials', 'compromised',
      'someone knows', 'left', 'leaver',
    ],
    routes: ['/'],
    audience: 'staff',
    steps: [
      {
        text: 'Click **Change device password**, under the profile icon. It changes the sign-in this machine is running on, which is yours.',
        target: ['account.change-password', 'account.profile-icon'],
      },
      {
        text: 'Nobody else is affected: colleagues reach their tiles with their own PINs. Your PIN is separate, and keeps working.',
      },
      {
        text: 'Enter the current password and the new one: at least 8 characters, with upper and lower case, a number and a symbol.',
      },
      {
        text: '**Two-factor authentication** sits beside it: a code from your phone as well as the password, so the password alone is not enough.',
        target: ['account.two-factor', 'account.profile-icon'],
      },
      {
        text: 'Both appear only when you are the person this machine is signed in as, since they change that account. **Sign out** below them stays for everyone.',
      },
    ],
    related: ['account.which-exit'],
  },
];
